import { useMemo, useState } from 'react'
import { useQuery, useSuspenseQuery } from '@tanstack/react-query'
import { createFileRoute, Link, notFound } from '@tanstack/react-router'
import type { ColumnDef } from '@tanstack/react-table'
import { BanIcon, CheckIcon, FileTextIcon, ReceiptIcon, SplitIcon, XIcon } from 'lucide-react'

import { ApiClientError } from '@ems/api-client/client'
import { Button } from '@ems/ui/components/button'
import { DataTable } from '@ems/ui/patterns/data-table'
import { EmptyState } from '@ems/ui/patterns/empty-state'
import { PageHeader } from '@ems/ui/patterns/page-header'
import { StatusBadge } from '@ems/ui/patterns/status-badge'
import { formatDateTime, formatMoney } from '@ems/ui/lib/format'

import { NotFoundView } from '#admin/components/not-found-view.tsx'
import { ReadOnlyNotice } from '#admin/components/read-only-notice.tsx'
import { sessionQuery } from '#admin/features/auth/queries.ts'
import { can } from '#admin/features/auth/session.ts'
import { AllocateDialog } from '#admin/features/billing/allocate-dialog.tsx'
import { label } from '#admin/features/billing/api.ts'
import { DocumentSkeleton } from '#admin/features/billing/billing-skeletons.tsx'
import { PaymentHistoryCard } from '#admin/features/billing/history-card.tsx'
import { Fact, Panel } from '#admin/features/billing/invoice-panels.tsx'
import { PayerName } from '#admin/features/billing/payer-name.tsx'
import {
  useAttachPaymentProof,
  useConfirmPayment,
  useRejectPayment,
  useVoidPayment,
} from '#admin/features/billing/mutations.ts'
import {
  fetchPaymentProofLink,
  methodLabel,
  payerOf,
  paymentTone,
  unallocated,
  type PaymentDetail,
} from '#admin/features/billing/payments-api.ts'
import { rateText } from '#admin/features/billing/exchange-rates-api.ts'
import { ProofUpload } from '#admin/features/billing/proof-upload.tsx'
import { paymentQuery } from '#admin/features/billing/queries.ts'
import { ConfirmDialog } from '@ems/ui/patterns/confirm-dialog'
import { NoteDialog } from '#admin/features/registrations/form-dialog.tsx'
import { openSignedLink } from '#admin/lib/open-signed-link.ts'

/** `/billing/payments/$paymentId` — one payment, and the invoices it settled. */
export const Route = createFileRoute('/_authenticated/billing/payments/$paymentId')({
  pendingComponent: DocumentSkeleton,
  staticData: { crumb: usePaymentCrumb },
  loader: async ({ context, params }) => {
    try {
      await context.queryClient.ensureQueryData(paymentQuery(params.paymentId))
    } catch (error) {
      if (error instanceof ApiClientError && error.status === 404 && !error.isMissingFeature) {
        throw notFound()
      }
      throw error
    }
  },
  notFoundComponent: NotFoundView,
  component: PaymentPage,
})

function usePaymentCrumb(): string {
  const { paymentId } = Route.useParams()
  return useQuery(paymentQuery(paymentId)).data?.reference ?? 'Payment'
}

type Allocation = PaymentDetail['allocations'][number]

function PaymentPage() {
  const { paymentId: id } = Route.useParams()
  const { data: payment } = useSuspenseQuery(paymentQuery(id))
  const { data: session } = useSuspenseQuery(sessionQuery())
  const canManage = can(session, 'billing.manage')
  const [dialog, setDialog] = useState<'confirm' | 'reject' | 'allocate' | 'void' | null>(null)
  const close = () => setDialog(null)
  const confirm = useConfirmPayment()
  const reject = useRejectPayment()
  const voiding = useVoidPayment()
  // A payment that is not void yet. The server refuses one that paid an invoice in full.
  const canVoid = payment.status === 'completed' || payment.status === 'pending'
  const left = unallocated(payment)
  const payer = payerOf(payment)
  const money = (amount: number) => formatMoney(amount, payment.currency)

  const columns = useMemo<ColumnDef<Allocation, never>[]>(
    () => [
      {
        id: 'invoice',
        header: 'Invoice',
        meta: { primary: true },
        cell: ({ row }) => (
          <Link
            to="/billing/invoices/$invoiceId"
            params={{ invoiceId: row.original.invoiceId }}
            className="font-mono text-xs after:absolute after:inset-0 hover:underline"
          >
            {row.original.invoiceNumber ?? 'draft'}
          </Link>
        ),
      },
      { accessorKey: 'billToName', header: 'Billed to', meta: { truncate: true } },
      {
        id: 'when',
        header: 'Allocated',
        cell: ({ row }) => (
          <span className="text-muted-foreground">{formatDateTime(row.original.createdAt)}</span>
        ),
      },
      {
        id: 'amount',
        header: 'Amount',
        meta: { align: 'end' },
        cell: ({ row }) => (
          <span className="flex flex-col items-end">
            <span>{formatMoney(row.original.amount, payment.currency)}</span>
            {row.original.invoiceCurrency === payment.currency ? null : (
              <span className="text-muted-foreground text-xs">
                = {formatMoney(row.original.convertedAmount, row.original.invoiceCurrency)}
                {row.original.appliedExchangeRate === null
                  ? null
                  : ` at ${rateText(row.original.invoiceCurrency, row.original.appliedExchangeRate, payment.currency)}`}
              </span>
            )}
          </span>
        ),
      },
    ],
    [payment.currency],
  )

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title={
          <span className="flex flex-wrap items-center gap-3">
            <span className="font-mono">{payment.reference}</span>
            <StatusBadge tone={paymentTone(payment.status)}>{label(payment.status)}</StatusBadge>
          </span>
        }
        description={`${money(payment.amount)} · ${methodLabel(payment.method)}${payer === null ? '' : ` · ${payer.name}`}`}
        actions={
          canManage ? (
            <>
              {payment.status === 'pending' ? (
                <>
                  <Button variant="outline" onClick={() => setDialog('reject')}>
                    <XIcon />
                    Mark failed
                  </Button>
                  <Button onClick={() => setDialog('confirm')}>
                    <CheckIcon />
                    Confirm received
                  </Button>
                </>
              ) : null}
              {payment.status === 'completed' && left > 0 ? (
                <Button onClick={() => setDialog('allocate')}>
                  <SplitIcon />
                  Allocate
                </Button>
              ) : null}
              {canVoid ? (
                <Button variant="outline" onClick={() => setDialog('void')}>
                  <BanIcon />
                  Void payment
                </Button>
              ) : null}
            </>
          ) : null
        }
      />

      {canManage ? null : <ReadOnlyNotice area="this payment" />}

      <dl className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        {[
          { term: 'Amount', value: money(payment.amount) },
          { term: 'Allocated', value: money(payment.allocatedAmount) },
          { term: 'Unallocated', value: money(left) },
        ].map((item) => (
          <div key={item.term} className="bg-card rounded-lg border border-(--border) p-4">
            <dt className="text-muted-foreground text-sm">{item.term}</dt>
            <dd className="text-xl font-medium tabular-nums">{item.value}</dd>
          </div>
        ))}
      </dl>

      <Panel title="Details">
        <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-2 text-sm sm:grid-cols-[auto_1fr_auto_1fr]">
          <Fact term="Method">{methodLabel(payment.method)}</Fact>
          <Fact term="Payer">{payer === null ? null : <PayerName payment={payment} />}</Fact>
          <Fact term="Received">
            {payment.paidAt === null ? 'Not yet' : formatDateTime(payment.paidAt)}
          </Fact>
          <Fact term="Recorded">{formatDateTime(payment.createdAt)}</Fact>
          <Fact term="Provider">{payment.provider}</Fact>
          <Fact term="Provider ref">
            {payment.providerRef === null ? null : (
              <span className="font-mono text-xs">{payment.providerRef}</span>
            )}
          </Fact>
          <Fact term="Virtual account">
            {payment.vaNumber === null ? null : (
              <span className="font-mono text-xs">{payment.vaNumber}</span>
            )}
          </Fact>
          <Fact term="Proof">
            <ProofCell
              paymentId={id}
              hasProof={payment.proofKey !== null}
              canAttach={canManage && can(session, 'identity.write')}
            />
          </Fact>
          {payment.voidedAt === null ? null : (
            <Fact term="Voided">
              {`${formatDateTime(payment.voidedAt)}${payment.voidReason === null ? '' : ` · ${payment.voidReason}`}`}
            </Fact>
          )}
          <Fact term="Raised for">
            {payment.raisedForInvoiceId === null ? null : (
              <Link
                to="/billing/invoices/$invoiceId"
                params={{ invoiceId: payment.raisedForInvoiceId }}
                className="underline"
              >
                Open invoice
              </Link>
            )}
          </Fact>
        </dl>
      </Panel>

      <DataTable
        caption="Invoices settled"
        title="Invoices settled"
        columns={columns}
        data={payment.allocations}
        getRowId={(allocation) => allocation.id}
        empty={
          <EmptyState
            icon={<ReceiptIcon />}
            title="Not allocated yet"
            description={
              payment.status === 'completed'
                ? 'Allocate it to the invoice it pays, and the invoice’s balance follows.'
                : 'Only a received payment can be allocated. Confirm it first.'
            }
          />
        }
      />

      <PaymentHistoryCard paymentId={id} />

      <ConfirmDialog
        open={dialog === 'confirm'}
        onOpenChange={(open) => (open ? undefined : close())}
        title={`Confirm ${payment.reference} was received?`}
        description={
          payment.raisedForInvoiceId === null
            ? `${money(payment.amount)} is marked received as of now. Allocate it to an invoice afterwards.`
            : `${money(payment.amount)} is marked received as of now and applied to the invoice it was raised for.`
        }
        confirmLabel="Confirm received"
        busy={confirm.isPending}
        onConfirm={() => confirm.mutate({ id }, { onSuccess: close })}
      />

      <NoteDialog
        open={dialog === 'reject'}
        onOpenChange={(open) => (open ? undefined : close())}
        title={`Mark ${payment.reference} as failed?`}
        description="It stays on record as failed and nothing is applied to any invoice. This cannot be undone."
        confirmLabel="Mark failed"
        noteLabel="Reason"
        placeholder="e.g. Transfer never arrived after 7 days."
        required
        destructive
        busy={reject.isPending}
        onConfirm={(reason) => reject.mutate({ id, reason }, { onSuccess: close })}
      />

      <NoteDialog
        open={dialog === 'void'}
        onOpenChange={(open) => (open ? undefined : close())}
        title={`Void ${payment.reference}?`}
        description="This will unallocate the payment from its invoices and restore their outstanding balances."
        confirmLabel="Void payment"
        noteLabel="Reason"
        placeholder="e.g. The payment was recorded twice."
        required
        destructive
        busy={voiding.isPending}
        onConfirm={(reason) => voiding.mutate({ id, reason }, { onSuccess: close })}
      />

      {dialog === 'allocate' ? <AllocateDialog payment={payment} onClose={close} /> : null}
    </div>
  )
}

/**
 * The proof of payment: a button that opens it and, for an operator who can upload, a button that
 * attaches a file or replaces it. The page saves the file when the upload is complete.
 */
function ProofCell({
  paymentId,
  hasProof,
  canAttach,
}: {
  paymentId: string
  hasProof: boolean
  canAttach: boolean
}) {
  const [opening, setOpening] = useState(false)
  const attach = useAttachPaymentProof()

  const open = async () => {
    setOpening(true)
    await openSignedLink(() => fetchPaymentProofLink(paymentId))
    setOpening(false)
  }

  if (!hasProof && !canAttach) return null
  return (
    <span className="flex flex-wrap items-center gap-2">
      {hasProof ? (
        <Button variant="outline" size="sm" disabled={opening} onClick={() => void open()}>
          <FileTextIcon />
          {opening ? 'Opening…' : 'View proof'}
        </Button>
      ) : null}
      {canAttach ? (
        <ProofUpload
          value=""
          attachLabel={hasProof ? 'Replace proof' : 'Attach proof'}
          onChange={(proofKey) => attach.mutate({ id: paymentId, proofKey })}
        />
      ) : null}
      {attach.isPending ? <span className="text-muted-foreground text-sm">Saving…</span> : null}
    </span>
  )
}
